import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import type { ConnectedFlightResponse, ListFilter } from '@/api/generated/schemas';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { useMagicFormEdit } from '@/shared/legacyMagicBridge';
import { Panel } from '@/shared/ui/panel';
import {
  RegularTransportSelector,
  TransportModeBadge,
  type RegularTransportRef,
} from '../../../../components/RegularTransportSelector';
import { formatMinutes } from '../model/format';

interface ConnectedFlightsPanelProps {
  flights: ConnectedFlightResponse[];
  /** Výchozí stanice cesty tam — rotace do ní musí dovézt. */
  hubStationId: number | null;
  hubCode: string | null;
  /** Pokrytí legu skutečnými spoji, klíčované `ticketId:facilityId`. */
  coverage: Map<string, { covered: number; total: number }>;
  /** Dopravy už ve schématu (v kterémkoliv směru) se znovu nenabízejí. */
  excludeIds: number[];
  readOnly: boolean;
  disabled: boolean;
  onAdd: (regularTransportId: number) => void;
  onRemove: (ticketId: string) => void;
}

/**
 * Spojené lety — celé vnitrostátní rotace, které svážejí na hub cesty tam.
 *
 * Panel se ukazuje i prázdný: bez něj by nebylo z čeho rotaci přidat. Dokud hlavní doprava nemá řetěz,
 * není znám hub, takže se místo tlačítka ukáže výzva doplnit nejdřív cestu tam.
 *
 * Cena rotace je **příplatek** — v matici má vlastní sloupec, ale do součtu „Celkem" nevstupuje.
 */
export function ConnectedFlightsPanel({
  flights,
  hubStationId,
  hubCode,
  coverage,
  excludeIds,
  readOnly,
  disabled,
  onAdd,
  onRemove,
}: ConnectedFlightsPanelProps) {
  const { t } = useTranslation('venua-planner');
  // Detail dopravy patří do stacku okna, ve kterém panel běží (osamostatněné okno ≠ plocha).
  const openFormEdit = useMagicFormEdit();

  const broken = flights.filter((flight) => flight.errors.length > 0).length;

  // Nabízí se jen letecká doprava, jejíž všechny legy jsou tuzemské a která umí dorazit do hubu.
  const filters: ListFilter[] = [
    { field: 'air' },
    { field: 'allDomestic' },
    { field: 'toStationId', number: hubStationId ?? 0 },
  ];
  if (excludeIds.length > 0) {
    filters.push({ field: 'excludeIds', ids: excludeIds.map(String) });
  }

  return (
    <Panel
      collapsible
      icon="plane"
      title={t('transportScheme.connected.title')}
      subtitle={t('transportScheme.connected.subtitle', { hub: hubCode ?? '—' })}
      actions={
        <div className="flex items-center gap-2">
          {broken > 0 && (
            <Badge appearance="soft" tone="danger">
              {t('transportScheme.direction.problems', { count: broken })}
            </Badge>
          )}
          {!readOnly && hubStationId != null ? (
            <RegularTransportSelector
              mode="single"
              trigger="button"
              addMode
              label={t('transportScheme.connected.title')}
              buttonVariant="secondary"
              buttonSize="sm"
              buttonLabel={t('transportScheme.connected.add')}
              extraFilters={filters}
              selected={[]}
              onChange={(items: readonly RegularTransportRef[]) => {
                const picked = items[0];
                if (picked && !disabled) onAdd(picked.regularTransportId);
              }}
            />
          ) : null}
        </div>
      }
    >
      {flights.length === 0 ? (
        <p className="op-connempty">
          {hubStationId == null
            ? t('transportScheme.connected.emptyNoHub')
            : t('transportScheme.connected.empty', { hub: hubCode ?? '—' })}
        </p>
      ) : (
        <div className="op-connflat">
          {flights.map((flight) => {
            const message = flight.errors
              .map((error) => t(`transportScheme.chainError.${error.code}`))
              .join(' · ');
            const gap = flight.legs.reduce((sum, leg) => {
              const legCoverage = coverage.get(`${leg.ticketId}:${leg.facilityId}`);
              return sum + (legCoverage ? legCoverage.total - legCoverage.covered : 0);
            }, 0);

            return (
              <div
                key={flight.ticketId}
                className="op-connrow"
                {...(flight.errors.length > 0 ? { 'data-err': '' } : {})}
              >
                <span className="op-route">
                  {flight.legs[0]?.fromCode ?? '—'}
                  <span className="ar">↔</span>
                  {flight.legs[0]?.toCode ?? '—'}
                </span>

                <TransportModeBadge mode={flight.mode} t={t} />

                {flight.errors.length > 0 ? (
                  <span className="op-err" title={message}>
                    !
                  </span>
                ) : null}

                <span className="op-conn-name" title={flight.regularTransportName}>
                  {flight.regularTransportName}
                </span>

                <span className="op-conn-legs">
                  {flight.legs.map((leg, index) => (
                    <Fragment key={`${leg.facilityId}-${index}`}>
                      {index > 0 ? <span className="op-cl-sep">·</span> : null}
                      <span className="op-cl">
                        <span className="rt">
                          {leg.fromCode ?? '—'} → {leg.toCode ?? '—'}
                        </span>
                        <span>
                          {leg.departureMinutes != null && leg.arrivalMinutes != null
                            ? `${formatMinutes(leg.departureMinutes)} – ${formatMinutes(leg.arrivalMinutes)}`
                            : '—'}
                        </span>
                        <span className="fl">{leg.flightCode ?? '—'}</span>
                      </span>
                    </Fragment>
                  ))}
                </span>

                <span className="op-conn-cov" {...(gap > 0 ? { 'data-warn': '' } : {})}>
                  {gap > 0 ? t('transportScheme.connected.gap', { count: gap }) : null}
                </span>

                <Button
                  size="icon-sm"
                  variant="ghost"
                  iconLeft="external-link"
                  title={t('transportScheme.chain.openTransport')}
                  onClick={() => openFormEdit('RegularTransport', flight.regularTransportId)}
                />

                {!readOnly && (
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    iconLeft="x"
                    className="hover:bg-phx-danger-tint hover:text-phx-danger"
                    title={t('transportScheme.connected.remove')}
                    onClick={() => onRemove(flight.ticketId)}
                  />
                )}

                {flight.errors.length > 0 ? <p className="op-errmsg">{message}</p> : null}
              </div>
            );
          })}
        </div>
      )}
    </Panel>
  );
}
